import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import MultiMediaUpload from "@/components/MultiMediaUpload";
import { isVideoPath } from "@/components/MediaViewer";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function VehicleEditModal({ vehicle, onClose, onSaved }) {
  const [plate, setPlate] = useState(vehicle.plate_number || "");
  const [location, setLocation] = useState(vehicle.location || "");
  const [notes, setNotes] = useState(vehicle.notes || "");
  const [tagMedia, setTagMedia] = useState(vehicle.tag_photo_paths || []);
  const isBooted = ["booted", "paid", "removed"].includes(vehicle.status);
  const [bootMedia, setBootMedia] = useState(vehicle.boot_photo_paths || []);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!plate.trim() || !location.trim()) { toast.error("Plate and location required"); return; }
    if (tagMedia.length < 3) { toast.error(`At least 3 tag photos/videos (${tagMedia.length}/3)`); return; }
    const body = { plate_number: plate, location, notes: notes || null, tag_photo_paths: tagMedia };
    if (isBooted) {
      const photos = bootMedia.filter((p) => !isVideoPath(p));
      const videos = bootMedia.filter((p) => isVideoPath(p));
      if (photos.length < 2 || videos.length < 1) { toast.error("Boot evidence needs 2 photos + 1 video"); return; }
      body.boot_photo_paths = bootMedia;
      body.boot_video_path = videos[0];
    }
    setBusy(true);
    try {
      const r = await api.patch(`/vehicles/${vehicle.id}`, body);
      toast.success(`Saved ${r.data.plate_number}`);
      onSaved?.(r.data);
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="edit-modal">
      <Card className="bg-[#131B2A] border-[#28364F] p-5 max-w-md w-full space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="text-xs uppercase tracking-widest text-cyan-300">Edit vehicle</div>
        <div>
          <Label>License plate</Label>
          <Input value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())}
            className="bg-[#0B0F17] border-[#28364F] text-white plate uppercase tracking-widest" data-testid="edit-plate-input" />
        </div>
        <div>
          <Label>Location</Label>
          <Input value={location} onChange={(e) => setLocation(e.target.value)}
            className="bg-[#0B0F17] border-[#28364F] text-white" data-testid="edit-location-input" />
        </div>
        <div>
          <Label>Notes</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
            className="bg-[#0B0F17] border-[#28364F] text-white" data-testid="edit-notes-input" />
        </div>
        <div>
          <Label>Tag photos / videos (at least 3)</Label>
          <MultiMediaUpload value={tagMedia} onChange={setTagMedia} min={3} testId="edit-tag-media" />
        </div>
        {isBooted && (
          <div>
            <Label>Boot evidence (at least 2 photos + 1 video)</Label>
            <MultiMediaUpload value={bootMedia} onChange={setBootMedia} minPhotos={2} minVideos={1} testId="edit-boot-media" />
          </div>
        )}
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1 bg-transparent border-[#28364F] text-white hover:bg-[#1C263B]"
            onClick={onClose} data-testid="edit-cancel-btn">Cancel</Button>
          <Button className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-[#0B0F17] font-bold" onClick={save} disabled={busy} data-testid="edit-save-btn">
            {busy ? "Saving..." : "Save changes"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
